/* eslint-disable react/no-array-index-key */
import 'antd/es/slider/style';
import React, { useCallback } from 'react';
import { Image, Tooltip, Upload } from 'antd';
import moment from 'moment-timezone';
import { CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { IconUpload, ImageMisc } from '../../../assets';
import { COMMON_TRANSLATION } from '../../../constants/define/translation';
import { UploadFileToBlobInterface } from '../../../utils/azure';
import { appStyles } from '../../../styles';
import { ButtonActionPage } from '../../atoms';

const { Dragger } = Upload;

interface DragDropProductImageProps {
  isView?: boolean;
  imageList: any[];
  messageErrorUploadImage?: string;
  selectedImage: number;
  onSetImageList: (data: UploadFileToBlobInterface[]) => void;
  onSelectedImage: (index: number) => void;
  maxImage?: number;
  multipleUpload?: boolean;
  nodePreview?: React.ReactNode;
  titleSize?: string;
  startTime: any;
  endTime: any;
  title: string;
}

export const DragDropImageTourTrash = (props: DragDropProductImageProps) => {
  const { t } = useTranslation(COMMON_TRANSLATION);
  const {
    isView,
    imageList,
    onSetImageList,
    messageErrorUploadImage,
    selectedImage,
    onSelectedImage,
    maxImage,
    multipleUpload,
    nodePreview,
    titleSize,
    title,
    startTime,
    endTime,
  } = props;

  const maxImageUploaded = maxImage || 5;

  const onRemoveImage = useCallback(
    index => {
      const cloneImageList = [...imageList];
      cloneImageList.splice(index, 1);
      onSetImageList(cloneImageList);
      let indexSelected = selectedImage;
      if (indexSelected > cloneImageList.length - 1) {
        indexSelected = cloneImageList.length - 1;
      }
      if (cloneImageList.length === 0) {
        indexSelected = 0;
      }
      onSelectedImage(indexSelected < 0 ? 0 : indexSelected);
    },
    [onSetImageList, imageList, selectedImage, onSelectedImage],
  );

  const onHandleViewPreview = useCallback(
    (index: number) => {
      onSelectedImage(index);
    },
    [onSelectedImage],
  );

  const propsDeep = {
    multiple: true,
    showUploadList: false,
    accept: 'image/*',
    beforeUpload(file: any): Promise<any> {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const img = document.createElement('img');
          img.src = reader?.result ? reader?.result.toString() : '';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0);
            }
            const url = canvas.toDataURL();
            canvas.toBlob(
              blob => {
                const uploadedFiles = [
                  ...imageList,
                  ...[
                    {
                      dataURL: url,
                      blob,
                      fileName: file.name,
                      fileType: file.type,
                      isImageURL: false,
                    },
                  ],
                ];
                onSetImageList(uploadedFiles);
                onSelectedImage(uploadedFiles.length - 1);

                const tootTip = document.querySelectorAll('.ant-tooltip');
                tootTip.forEach(r => r.classList.add('ant-tooltip-hidden'));
                resolve(blob);
              },
              file.type || 'image/jpeg',
              1,
            );
          };
        };
      });
    },
  };

  const formatDate = (strDate: any) => {
    if (!strDate) return '';
    const dt = moment(strDate);
    return dt.format('MM.DD');
  };

  const parseString = (str: string) => {
    const num: number = str.length;
    if (num > 30) {
      const newStr: string = str.substring(0, 30);
      return `${newStr}...`;
    }
    return str;
  };

  return (
    <>
      <StyledBox>
        {imageList.filter(img => !img.isLogo).length > 0 ? (
          <>
            <div className={`image-crop ${multipleUpload ? 'multipleUpload' : ''}`}>
              {multipleUpload && (
                <div className="previewImage" style={{ position: 'relative' }}>
                  <Image className="image" src={imageList[selectedImage].dataURL} alt="imagePreview" preview={false} />
                  <div className="viewTitle">
                    {startTime && endTime && (
                      <div className="dateTimeText">{`${formatDate(startTime)} - ${formatDate(endTime)}`}</div>
                    )}
                    {title && <div className="titleText">{parseString(title)}</div>}
                    {title && <ButtonActionPage id="1" text="ENTER TO WIN" className="btn-learn-more" />}
                  </div>
                </div>
              )}
              {!multipleUpload && nodePreview}
            </div>
            <div className="bottom-horizontal">
              {imageList.length < maxImageUploaded && !isView && (
                <Tooltip title={t('uploadImage')} placement="top">
                  <div className="upload-continue">
                    <Dragger {...propsDeep} className="drag-continue">
                      <div className="icon-upload-continue">
                        <IconUpload />
                      </div>
                    </Dragger>
                  </div>
                </Tooltip>
              )}
              <div className="imgContainer">
                {imageList.map((img: any, index: number) => (
                  <div className="thumbPreview" key={index}>
                    <div
                      className={`${selectedImage === index ? 'active-thumb' : ''} ${
                        img?.isLogo ? 'thumbLogoPreviewInner' : 'thumbPreviewInner'
                      }`}
                      onClick={() => onHandleViewPreview(index)}
                      onKeyPress={() => onHandleViewPreview(index)}
                      tabIndex={0}
                      role="button"
                    >
                      <Image className="thumb-preview" src={img.dataURL} alt="imagePreview" preview={false} />
                    </div>
                    {!isView && (
                      <div
                        className="icon-remove-image"
                        onClick={() => onRemoveImage(index)}
                        onKeyPress={() => onRemoveImage(index)}
                        role="button"
                        tabIndex={0}
                      >
                        <CloseCircleOutlined className={`${selectedImage === index ? 'active-icon' : ''}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <Dragger {...propsDeep} className={!multipleUpload ? 'onlyUploadAImage' : ''}>
            {multipleUpload && (
              <>
                <div className="shelf shelf-left-top" />
                <div className="shelf shelf-right-top" />
                <div className="shelf shelf-left-bottom" />
                <div className="shelf shelf-right-bottom" />
                <div className="box">
                  <Image className="image-gift" src={ImageMisc} alt="ImageMisc" preview={false} />
                  <span>
                    {t('dragMessage')} {titleSize}
                  </span>
                </div>
              </>
            )}
            {!multipleUpload && nodePreview}
          </Dragger>
        )}
        {messageErrorUploadImage && (
          <div className="error-upload">
            <WarningOutlined />
            {messageErrorUploadImage}
          </div>
        )}
      </StyledBox>
    </>
  );
};

const StyledBox = styled.div`
  ${appStyles.fullWidthHeight}

  .ant-upload.ant-upload-drag {
    border: none;
    background: #00000010 0% 0% no-repeat padding-box;
  }

  & div.box {
    position: absolute;
    left: 25%;
    top: 35%;
    right: 25%;
    transform: translateY(-39%);
    height: 180px;
    min-height: 150px;
    background: #00000010 0% 0% no-repeat padding-box;
    & > span {
      text-align: center;
      margin-top: 2rem;
      display: flow-root;
    }

    & .ant-image {
      ${appStyles.fullWidthHeight}
      & img.image-gift {
        ${appStyles.fullWidthHeight}
        padding: 25% 27%;
      }
    }
  }
  .bottom-horizontal {
    display: flex;
  }

  & div.upload-continue {
    height: 80px;
    width: 80px;
    vertical-align: top;
    display: inline-flex;
    align-items: end;
    border: 1px solid #fff;
    margin: 1.7rem 0 0 0.6rem;
    ${appStyles.commonBorderRadius}
    ${appStyles.background.bgUploadImage}
    & .ant-upload {
      padding: 0;
    }
    & > span {
      ${appStyles.fullWidthHeight}
    }
  }
  .imgContainer {
    overflow: auto;
    display: flex;
    flex: 1;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }

  & .image-crop {
    height: 75%;
    min-height: 75%;
  }

  & .multipleUpload {
    height: 15%;
    & .ant-image {
      margin: 1rem;
      & img {
        ${appStyles.borderRadiusPreview}
      }
    }
  }

  & div.shelf {
    background: transparent;
    position: absolute;
    width: 24px;
    height: 24px;
    border: solid #666;
    display: inline-block;
  }

  & div.shelf-left-top {
    top: 19%;
    left: 22%;
    border-width: 5px 0 0 5px;
  }

  & div.shelf-right-top {
    top: 19%;
    left: 71%;
    border-width: 5px 5px 0 0;
  }

  & div.shelf-left-bottom {
    top: 54%;
    left: 22%;
    border-width: 0 0 5px 5px;
  }

  & div.shelf-right-bottom {
    top: 54%;
    left: 71%;
    border-width: 0 5px 5px 0;
  }

  & img.preview {
    width: 70%;
    height: auto;
    ${appStyles.background.white}
    margin: 15%;
    padding: 1rem;
  }

  & .thumbPreview {
    display: inline-flex;
    box-sizing: border-box;
    cursor: pointer;
    margin: 2rem 0 0 0.7rem;

    position: relative;
    & .thumbPreviewInner {
      display: flex;
      min-width: 0;
      overflow: hidden;
      max-width: 100px;
      height: 70px;
      padding: 2px;
      border: 1px solid #d9d9d9;
      border-radius: 2px;
      & .ant-image {
        position: relative;
        display: flex;
        align-items: center;
      }
      & img {
        display: block;
        max-height: 100%;
        max-width: 100%;
        width: auto;
        overflow: hidden;
        padding: 2px;
      }
    }
    & .thumbLogoPreviewInner {
      display: flex;
      min-width: 0;
      overflow: hidden;
      max-width: 35px;
      height: 35px;
      padding: 2px;
      border: 1px solid #d9d9d9;
      border-radius: 2px;
      margin-left: 35px;
      & .ant-image {
        position: relative;
        display: flex;
        align-items: center;
      }
      & img {
        display: block;
        max-height: 100%;
        max-width: 100%;
        width: auto;
        overflow: hidden;
        padding: 2px;
      }
    }
    & .icon-remove-image .anticon-close-circle {
      position: absolute;
      top: -0.5rem;
      right: -0.4rem;
    }
  }

  & .error-upload {
    ${appStyles.color.error}
    position: absolute;
    top: 2%;
    left: 22%;
    font-size: 14px;
    & .anticon {
      margin-right: 10px;
    }
  }

  & .active-thumb {
    outline: 1px solid #1ec45f;
  }

  & .ant-upload-drag-container {
    width: 100%;
  }

  & .onlyUploadAImage {
    & .ant-upload-drag-container {
      display: block !important;
    }
    &.ant-upload.ant-upload-drag .ant-upload {
      padding: 0;
    }
  }

  .previewImage {
    height: 370px;
    max-height: 370px;
    width: 100%;
    & .image {
      height: 370px;
      max-height: 370px;
      width: 100%;
    }
    & .viewTitle {
      position: absolute;
      bottom: -15px;
      left: 5%;
      backdrop-filter: blur(2px);
      width: 90%;
      padding-bottom: 20px;
      & .dateTimeText {
        padding-top: 5px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 1.62px;
        color: white;
        margin-left: 5%;
      }
      & .titleText {
        margin-left: 5%;
        width: 80%;
        font-weight: 800;
        color: white;
        font-size: 22px;
        letter-spacing: 1.1px;
        margin-bottom: 8px;
      }
      & .btn-learn-more {
        background: white;
        color: black;
        border: white;
        font-weight: 600;
        border-radius: 25px;
        width: auto;
        height: 2.3rem;
        margin-left: 5%;
      }
    }
  }
`;
